import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import { EnviarEmailHeteroidentificacao } from '../../viewmodel/ComunicacaoCandidato/EmailHeteroidentificacao';

interface FinalizarBancaProps {
    show: boolean;
    onHide: () => void;
    bancaId: string;
    bancaFinalizada?: boolean;
    setBancaFinalizada?: React.Dispatch<React.SetStateAction<boolean>>
}

const FinalizarBancaModal: React.FC<FinalizarBancaProps> = ({ show, onHide, bancaId, bancaFinalizada = false, setBancaFinalizada }) => {
    const [counter, setCounter] = useState<number>(4);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [ocorrencias, setOcorrencias] = useState<string[]>([]);
    const [novaOcorrencia, setNovaOcorrencia] = useState<string>('');
    const inputRef = React.useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (show) {
            setCounter(6);
            const timer = setInterval(() => {
                setCounter((prevCounter) => prevCounter - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [show]);

    useEffect(() => {
        if (counter === 0) {
            setIsSubmitting(false);
        }
    }, [counter]);

    const handleAddOcorrencia = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        if (novaOcorrencia.trim() === '') {
            toast.error('A ocorrência não pode estar vazia.');
            return;
        }

        setOcorrencias([...ocorrencias, novaOcorrencia]);
        setNovaOcorrencia('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleRemoveOcorrencia = (index: number) => {
        setOcorrencias(ocorrencias.filter((_, i) => i !== index));
    };

    const handleFinalizar = async (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsSubmitting(true);
        try {
            const todasOcorrencias = [...ocorrencias];
            if (novaOcorrencia.trim() !== '') {
                todasOcorrencias.push(novaOcorrencia.trim());
            }
            
            if (todasOcorrencias.length === 0) {
                await BancaViewModel.finalizarBanca(bancaId, '(sem ocorrências)');
            } else {
                await BancaViewModel.finalizarBanca(bancaId, todasOcorrencias.join('; '));
            }
            toast.success('Banca finalizada com sucesso!');
            setBancaFinalizada?.(true);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMessage = error.response?.data[0]?.message || 'Erro ao finalizar banca';
            toast.error(`${errorMessage}`);

        } finally {
            setIsSubmitting(false);
            handleClose();
        }
    };

    const handleClose = () => {
        setIsSubmitting(false);
        setCounter(0);
        setOcorrencias([]);
        setNovaOcorrencia('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
            <Modal.Header closeButton className="bg-danger text-white">
                <Modal.Title className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {bancaFinalizada ? 'Finalizar Banca Novamente' : 'Finalizar Banca'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="alert alert-warning mb-4">
                    <strong>Atenção:</strong> Esta ação finalizará definitivamente a banca e enviará os resultados aos candidatos.
                </div>

                {ocorrencias.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">Ocorrências Registradas</h6>
                        </div>
                        <div className="card-body" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {ocorrencias.map((ocorrencia, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                    <span className="flex-grow-1">{ocorrencia}</span>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveOcorrencia(index)}>
                                        ×
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mb-4">
                    <Form.Label className="fw-bold">
                        {ocorrencias.length === 0 ? 'Registrar Ocorrências (opcional)' : 'Adicionar Mais Ocorrências'}
                    </Form.Label>
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        value={novaOcorrencia}
                        onChange={(e) => setNovaOcorrencia(e.target.value)}
                        placeholder="Descreva alguma ocorrência durante a banca..."
                        onKeyPress={(e) => e.key === 'Enter' && handleAddOcorrencia(e)}
                        className="mb-2"
                    />
                    {novaOcorrencia.trim() && (
                        <div className="d-flex justify-content-center">
                            <Button 
                                variant="success" 
                                size="sm"
                                onClick={handleAddOcorrencia}
                                className="shadow-sm"
                            >
                                + Adicionar nova ocorrência
                            </Button>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button 
                    variant="danger" 
                    onClick={handleFinalizar} 
                    disabled={counter > 0 || isSubmitting}
                    className="px-4"
                >
                    {isSubmitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Finalizando...
                        </>
                    ) : (
                        <>
                            {counter > 0 ? `Aguarde ${counter}s` : 
                                (bancaFinalizada ? 'Finalizar Novamente' : 'Finalizar Banca')}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FinalizarBancaModal;
